import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function LeaderboardPage({ user }) {
  const [tab, setTab] = useState('monthly'); // 'monthly' or 'alltime'
  const [leaderboard, setLeaderboard] = useState([]);
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [currentUserEntry, setCurrentUserEntry] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const token = localStorage.getItem('token');

  const checkAdminStatus = useCallback(async () => {
    if (!token) {
      setIsAdmin(false);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE}/api/me/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAdmin(response.data.isAdmin || false);
    } catch (err) {
      setIsAdmin(false);
    }
  }, [token]);

  const fetchLeaderboardStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/leaderboard/status`);
      setIsLocked(response.data.locked || false);
    } catch (err) {
      // Error fetching leaderboard status
    }
  }, []);

  const fetchLeaderboard = useCallback(async (type, pageNum) => {
    setLoading(true);
    setError('');
    try {
      const endpoint = type === 'monthly' 
        ? `${API_BASE}/api/leaderboard/monthly` 
        : `${API_BASE}/api/leaderboard/alltime`;
      
      const config = {};
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` };
      }
      
      const response = await axios.get(endpoint, {
        params: { page: pageNum, limit, _t: Date.now() }, // Add cache-busting parameter
        ...config,
      });
      
      setLeaderboard(response.data.leaderboard || []);
      setTotal(response.data.total || 0);
      setPage(pageNum);
      
      if (response.data.month) {
        setMonth(response.data.month);
      }
      
      if (response.data.currentUserEntry) {
        setCurrentUserEntry(response.data.currentUserEntry);
      } else {
        setCurrentUserEntry(null);
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.locked) {
        setIsLocked(true);
        setError('Not enough data to view leaderboard');
      } else {
        setError(err.response?.data?.error || 'Failed to load leaderboard');
      }
    } finally {
      setLoading(false);
    }
  }, [token, limit]);

  const handleToggleLock = async () => {
    if (!isAdmin) return;
    
    setLockLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/leaderboard/toggle-lock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsLocked(response.data.locked);
      // Refresh leaderboard if unlocked
      if (!response.data.locked) {
        fetchLeaderboard(tab, page);
      }
    } catch (err) {
      alert('Failed to toggle leaderboard lock');
    } finally {
      setLockLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboardStatus();
    fetchLeaderboard(tab, 1);
    checkAdminStatus();
  }, [tab, fetchLeaderboardStatus, fetchLeaderboard, checkAdminStatus]);

  const handleNextPage = () => {
    if (page * limit < total) {
      fetchLeaderboard(tab, page + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      fetchLeaderboard(tab, page - 1);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-header">
        <div className="leaderboard-title-row">
          <h1>🏆 Leaderboard</h1>
          {isAdmin && (
            <button
              className={`lock-btn ${isLocked ? 'locked' : 'unlocked'}`}
              onClick={handleToggleLock}
              disabled={lockLoading}
              title={isLocked ? 'Unlock leaderboard' : 'Lock leaderboard'}
            >
              {isLocked ? '🔒' : '🔓'}
            </button>
          )}
        </div>
        <div className="leaderboard-tabs">
          <button
            className={`tab-btn ${tab === 'monthly' ? 'active' : ''}`}
            onClick={() => setTab('monthly')}
            disabled={isLocked}
          >
            This Month
          </button>
          <button
            className={`tab-btn ${tab === 'alltime' ? 'active' : ''}`}
            onClick={() => setTab('alltime')}
            disabled={isLocked}
          >
            All Time
          </button>
        </div>
        {month && <p className="month-label">{month}</p>}
      </div>

      {/* User's Current Rank (LeetCode style) */}
      {!loading && currentUserEntry && (
        <div className="user-rank-card">
          <div className="user-rank-label">Your Global Rank</div>
          <div className="user-rank-content">
            <div className="rank-badge">{currentUserEntry.rank}</div>
            <div className="rank-details">
              <div className="rank-name">{currentUserEntry.name}</div>
              <div className="rank-points">{currentUserEntry.points.toLocaleString()} points</div>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="loading">Loading leaderboard...</div>}
      {error && (
        <div className={`error-message ${isLocked ? 'locked-message' : ''}`}>
          {error}
        </div>
      )}

      {isLocked && !loading && (
        <div className="locked-leaderboard-message">
          <div className="locked-icon">🔒</div>
          <p>Not enough data to view leaderboard</p>
        </div>
      )}

      {!loading && !isLocked && leaderboard.length > 0 && (
        <div className="leaderboard-container">
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th className="rank-col">Rank</th>
                <th className="name-col">Name</th>
                <th className="points-col">Points</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, index) => (
                <tr key={`${entry.name}-${entry.rank}-${index}`} className={`rank-${entry.rank}`}>
                  <td className="rank-col">
                    {entry.rank === 1 && '🥇'}
                    {entry.rank === 2 && '🥈'}
                    {entry.rank === 3 && '🥉'}
                    {entry.rank > 3 && `#${entry.rank}`}
                  </td>
                  <td className="name-col">{entry.name}</td>
                  <td className="points-col">{entry.points.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Controls */}
          <div className="leaderboard-pagination">
            <button 
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={page === 1}
            >
              ← Previous
            </button>
            <div className="pagination-info">
              Page {page} of {totalPages} ({total} total users)
            </div>
            <button 
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={page >= totalPages}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {!loading && !isLocked && leaderboard.length === 0 && !error && (
        <div className="no-data">No leaderboard data yet. Start voting!</div>
      )}
    </div>
  );
}
